/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
/* eslint-disable import/named */
import React, { memo, useCallback, useEffect, useState } from 'react';

import { measure } from '@palette.dev/browser';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { changeOrderLeverage, getOrderLeverage } from 'actions/orderbook';
import { updateMarginMode, updateTradePreference } from 'actions/trade';
import Leverage from 'components/leverage';
import AccountMarginMode from 'components/leverage/accountMarginMode';
import MaxNotional from 'components/leverage/MaxNotional';
import OrderSectionPopoverContent from 'components/popover/OrderSectionPopoverContent';
import RangeSlider from 'components/rangeSlider';
import Render from 'components/render';
import { RenderForGlobalExchange } from 'components/render/RenderByExchangeType';
import {
  IS_INDIAN_EXCHANGE,
  MARGIN_TYPES,
  PORTFOLIO_MARGIN_GUIDE_LINK,
} from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { getFloor } from 'helpers/formulas';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { cropAfterDecimals, isMobileOrTablet, parseErrorMsg } from 'helpers/utils';
import useLeverage from 'hooks/componentHooks/useLeverage';
import useTheme from 'hooks/useTheme';
import useTradeTabMobile from 'hooks/useTradeTabMobile';
import useWindowSize from 'hooks/useWindowSize';
import {
  allOpenOrdersSelector,
  allOpenPositionsSelector,
} from 'selectors/holdingsSelectors';
import {
  allPrefrencesSelector,
  enabledPortfolioAssetsSelector,
  userSelector,
} from 'selectors/otherSelectors';
import { useAppDispatch } from 'storeHooks';
import { AngleDown, Info, LearnMoreIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';
import InputBase from 'UIKit/InputBase';
import MaterialConfirmDialog from 'UIKit/MaterialConfirmDialog';
import MaterialDialog from 'UIKit/MaterialDialog';
import MuiSwitch from 'UIKit/MuiSwitch';
import { PopoverTooltip } from 'UIKit/PopoverMaterial';
import PopoverWithCustomIcon from 'UIKit/PopoverWithCustomIcon';

import CrossPlaceOrderSection from './Cross';
import styles from './index.module.scss';
import { PortfolioMargin } from './Portfolio';

const InputComponent = memo(
  ({ toggleToSubmitBtn, onInputChange, leverage, isLightTheme }) => {
    return (
      <InputBase
        autoFocus
        data-testid="leverageInputField"
        onChange={event => toggleToSubmitBtn(onInputChange, event)}
        value={cropAfterDecimals(leverage, 2)}
        type="text"
        classes={{
          input: styles.leverageInput,
          root: cx(styles.leverageInputRoot, {
            [styles.leverageInputLightTheme]: isLightTheme,
          }),
        }}
        inputProps={{
          type: 'number',
          pattern: '[0-9]*',
          inputMode: 'numeric',
        }}
        endAdornment="x"
      />
    );
  }
);

const ChangeMarginModePopUp = ({
  showModal,
  handleMarginModeClose,
  updateMarginModeFun,
  marginType,
  isolatedError,
  resetIsolatedError,
}) => {
  const { isMobile } = useWindowSize();

  return (
    <MaterialDialog
      showCancel={false}
      marginTopInherit={!isMobileOrTablet()}
      transition={isMobileOrTablet()}
      fullWidth={isMobile}
      title=""
      width={400}
      open={showModal}
      contentClassName={styles.leverageModal}
      onClose={handleMarginModeClose}
    >
      {/* eslint-disable-next-line test-selectors/onSubmit */}
      <AccountMarginMode
        onCancel={handleMarginModeClose}
        onSubmit={updateMarginModeFun}
        marginType={marginType}
        isolatedError={isolatedError}
        resetIsolatedError={resetIsolatedError}
        showCrossIcon
      />
    </MaterialDialog>
  );
};

const LeverageDropdown = ({ selectedLeverage, selectedProduct }) => {
  const [expand, setExpand] = useState(false);
  const { t } = useTranslation(['trading', 'portfolioMargin']);
  const { isLightTheme } = useTheme();
  const user = useSelector(userSelector);
  const isTradeTabMobile = useTradeTabMobile();
  const { isMobile } = useWindowSize();
  const openPositions = useSelector(allOpenPositionsSelector);
  const openOrders = useSelector(allOpenOrdersSelector);

  const hasActiveOrderOrPosition =
    [...openPositions, ...openOrders].filter(
      order =>
        order.product.symbol === selectedProduct.symbol &&
        order.product.contract_type === selectedProduct.contract_type
    ).length > 0;

  const marginType = user.margin_mode || MARGIN_TYPES.ISOLATED;
  const isIsolatedOrCrossMarginMode = [
    MARGIN_TYPES.ISOLATED,
    MARGIN_TYPES.CROSS,
  ].includes(marginType);

  const {
    leverage,
    maxLeverage,
    sliderValues: leverages,
    onLeverageChange,
    onInputChange,
    handleLeverageSubmit,
    enableSubmit,
    setEnabledSubmit,
  } = useLeverage({ product: selectedProduct });

  const toggleExpand = () => {
    measure.start('OrderPlacement_LeverageButtonDropdownClick');
    const isClosing = !expand;
    setExpand(prev => !prev);
    onLeverageChange(selectedLeverage);
    if (isClosing) {
      const { symbol, contract_type: contractType } = selectedProduct;
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.LEVERAGE_BUTTON_CLICK, {
        source: 'Single Order',
        current_leverage: selectedLeverage,
        product_name: symbol,
        product_type: contractType,
      });
    }
    measure.end('OrderPlacement_LeverageButtonDropdownClick');
  };

  const toggleToSubmitBtn = (updaterFun, leverageValue = null) => {
    setEnabledSubmit(true);
    if (leverageValue) {
      updaterFun(leverageValue);
    } else {
      updaterFun();
    }
  };

  const onLeverageSubmit = () => {
    measure.start('OrderPlacement_SetLeverageButtonClick');
    setExpand(false);
    handleLeverageSubmit();
    const { symbol, contract_type: contractType } = selectedProduct;
    trackMixpanelEvent('Leverage Change Accepted - Trade - Ordering', {
      product_name: symbol,
      product_type: contractType,
    });
    measure.end('OrderPlacement_SetLeverageButtonClick');
  };

  const LeverageWrapperComponent = isMobile
    ? MaterialConfirmDialog
    : ({ children }) => children;

  return (
    <Box
      renderWhen={isIsolatedOrCrossMarginMode}
      className={cx(styles.leverageDropDownWrapper, {
        [styles.inTradeTabMobile_leverageDropDownWrapper]: isTradeTabMobile,
      })}
    >
      <Box
        className={cx(styles.container, {
          [styles.lightContainer]: expand && isLightTheme,
        })}
      >
        <Box
          className={cx(styles.leverageLabel, {
            [styles.inTradeTabMobile_leverageLabel]: isTradeTabMobile,
          })}
          onClick={toggleExpand}
          data-testid="LeverageSelectionAccordion"
        >
          <Box className={styles.leftPart}>
            <Box className={styles.label}>
              {t('trading:leverage')}
              {/* <Box
                class={styles.infoIcon}
                renderWhen={expand}
                data-testid="LeverageSelectionInfoIcon"
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                <Tooltip
                  title={<Alert width={12} height={12} color="#BABABA" />}
                  body={t('portfolioMargin:cross_leverage_warning')}
                  titleClass={styles.toolTipTitle}
                  bodyClass={styles.toolTipBody}
                />
              </Box> */}
            </Box>

            <Box renderWhen={!expand} className={styles.leverageValue}>{`${getFloor(
              selectedLeverage
            )}x`}</Box>
          </Box>
          <Box className={cx(styles.rightPart, { [styles.expand]: expand })}>
            <AngleDown />
          </Box>
        </Box>
        <Render when={expand}>
          <LeverageWrapperComponent
            marginTopInherit={isMobileOrTablet()}
            transition={isMobileOrTablet()}
            fullWidth={isMobile}
            open={expand}
            variant="v2"
            showCancel={false}
            title={t('trading:setLeverage')}
            onClose={toggleExpand}
            onCancel={toggleExpand}
            contentClassName={styles.leverageMenuModal}
            headerClassName={styles.leverageModalHeader}
            containerClassName={styles.modalContentContainer}
            bodyClassName={styles.leverageModalBody}
            hideConfirmButton
          >
            <Box
              className={cx(styles.leverageMenu, {
                [styles.inTradeTabMobile_leverageMenu]: isTradeTabMobile,
              })}
            >
              <Render when={isMobile}>
                <Box className={styles.leverageInputLabel}>
                  <span>{t('trading:leverage')}</span>
                </Box>
              </Render>
              <Box className={styles.leverageInputWrapper}>
                <InputComponent
                  toggleToSubmitBtn={toggleToSubmitBtn}
                  onInputChange={onInputChange}
                  leverage={leverage}
                  isLightTheme={isLightTheme}
                />
              </Box>

              <Box
                className={cx(styles.leverageSliderWrapper, {
                  [styles.inTradeTabMobile_leverageSliderWrapper]: isTradeTabMobile,
                })}
              >
                <RangeSlider
                  useV2
                  isDarkTheme={!isLightTheme}
                  data-testid="leverageSlider"
                  onChange={value => toggleToSubmitBtn(onLeverageChange, value)}
                  maxLeverage={maxLeverage}
                  initialValue={Number(Math.floor(leverage))}
                  leverages={leverages}
                />
              </Box>
              <Box
                className={cx(styles.maxPosition, {
                  [styles.inTradeTabMobile_maxPosition]: isTradeTabMobile,
                })}
              >
                <Box>
                  <PopoverTooltip>
                    <PopoverTooltip.Anchor className={styles.label}>
                      {`${t('trading:maxPositionAt')} ${leverage}x`}
                    </PopoverTooltip.Anchor>
                    <PopoverTooltip.Content>
                      {t('trading:leverageSection.max_leverage_tooltip')}
                    </PopoverTooltip.Content>
                  </PopoverTooltip>
                </Box>
                <Box className={styles.value}>
                  <MaxNotional leverage={leverage} selectedProduct={selectedProduct} />
                </Box>
              </Box>
              <Render
                when={marginType === MARGIN_TYPES.CROSS && hasActiveOrderOrPosition}
              >
                <Box className={styles.leverageWarning}>
                  <span className={styles.infoIcon}>
                    <Info viewBox="0 0 16 16" />
                  </span>
                  <span className={styles.leverageWarningText}>
                    {t('trading:leverageChangeAppliesToPositions')}
                  </span>
                </Box>
              </Render>
              <Box className={styles.leverageSubmitBtn}>
                <DeltaButton
                  fullWidth
                  data-testid="leverageSubmitButton"
                  onClick={onLeverageSubmit}
                  disabled={!enableSubmit}
                  text={t('trading:setTo', { amount: `${leverage}x` })}
                />
              </Box>
            </Box>
          </LeverageWrapperComponent>
        </Render>
      </Box>
    </Box>
  );
};

const LeverageSection = ({
  selectedLeverage,
  selectedProduct,
  side,
  productType,
  productName,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showLeverageModal, setShowLeverageModal] = useState(false);
  const [isolatedError, setIsolatedError] = useState({});
  const { isMobile } = useWindowSize();

  const user = useSelector(userSelector);
  const marginType = user.margin_mode || MARGIN_TYPES.ISOLATED;
  const allPreferences = useSelector(state => allPrefrencesSelector(state));
  const eligiblePortfolios = useSelector(enabledPortfolioAssetsSelector);

  const maxLeverage = 100 / parseInt(selectedProduct.initial_margin, 10);

  const { t } = useTranslation(['trading', 'common', 'portfolioMargin']);
  const dispatch = useAppDispatch();

  const handleMarginModeClose = useCallback(() => {
    setShowModal(false);
    const { symbol, contract_type } = selectedProduct;
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.MARGIN_REJECT, {
      product_name: symbol,
      product_type: contract_type,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarginModeOpen = useCallback(() => {
    setShowModal(true);
    localStorage.setItem(`PORTFOLIO_MARGIN_HIGHLIGHT10`, 'false');
  }, []);

  const handleLeverageModalClose = () => {
    setShowLeverageModal(false);
    const { symbol, contract_type } = selectedProduct;
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.LEVERAGE_REJECT, {
      product_name: symbol,
      product_type: contract_type,
    });
  };

  const getSelectedProductLeverage = () => {
    dispatch(getOrderLeverage(selectedProduct.id));
  };

  const resetIsolatedError = () => {
    setIsolatedError({});
  };

  const updateMarginModeFun = obj => {
    resetIsolatedError();
    const payload = {
      margin_mode: obj.margin_mode,
      user_id: user?.id,
    };
    // console.log('leverageSection', payload);
    dispatch(updateMarginMode(payload))
      .then(() => {
        getSelectedProductLeverage();
        trackMixpanelEvent('Account Margin Mode Changed', {
          account_name: user.account_name,
          margin_mode: obj.margin_mode,
        });
        setShowModal(false);
      })
      .catch(err => {
        const { error } = parseErrorMsg(err);
        setIsolatedError({
          code: error?.code,
          context: error?.context,
        });
      });
  };

  useEffect(() => {
    getSelectedProductLeverage();
  }, []);

  useEffect(() => {
    if (selectedLeverage > maxLeverage) {
      dispatch(changeOrderLeverage(selectedProduct.id, maxLeverage));
    }
  }, [selectedLeverage, dispatch, maxLeverage, selectedProduct.id]);

  const handleAutoMarginChange = useCallback(
    e => {
      const { checked } = e.target;
      dispatch(updateTradePreference({ default_auto_topup: checked }));
    },
    [dispatch]
  );

  const isOptionsBuyAction =
    (selectedProduct.contract_type === 'call_options' ||
      selectedProduct.contract_type === 'put_options' ||
      selectedProduct.contract_type === 'move_options' ||
      selectedProduct.contract_type === 'options_combos') &&
    side === 'buy';

  const isPortfolio = marginType === MARGIN_TYPES.PORTFOLIO;

  const trackMixpanelEventLeverageButton = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.LEVERAGE_BUTTON_CLICK, {
      source: 'Single Order',
      current_leverage: `${getFloor(selectedLeverage)}x`,
      product_type: productType,
      product_name: productName,
    });
  };

  const trackMixpanelEventMarginButton = () => {
    const current_margin_mode =
      marginType === MARGIN_TYPES.ISOLATED
        ? t(`trading:openPositionCols.isolated`)
        : t(`trading:openPositionCols.cross`);

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.MARGIN_BUTTON_CLICK, {
      source: 'Single Order',
      current_margin_mode,
      product_type: productType,
      product_name: productName,
    });
  };

  const handleMarginClick = () => {
    setShowModal(true);
    trackMixpanelEventMarginButton();
  };

  const handleLeverageClick = () => {
    setShowLeverageModal(true);
    trackMixpanelEventLeverageButton();
  };

  const leverageSectionClassNames = cx(
    `${styles.leverageSection} flex--content-between align-items-center`,
    {
      'flex-set': !isPortfolio,
      [styles.leverageSectionIndianExchange]: IS_INDIAN_EXCHANGE,
    }
  );

  return (<>
    <div className={leverageSectionClassNames} data-palette="LeverageSection">
      <Render when={!isPortfolio}>
        <>
          {/* <MediaQuery query={device.up.md}> */}
          <div className={styles.container} data-palette="LeverageSection">
            <DeltaButton
              data-testid="margin-modal"
              onClick={handleMarginClick}
              className={styles.marginBtn}
              text={
                marginType === MARGIN_TYPES.ISOLATED
                  ? t(`trading:openPositionCols.isolated`)
                  : t(`trading:openPositionCols.cross`)
              }
            />
            <Render when={marginType !== MARGIN_TYPES.CROSS && !isOptionsBuyAction}>
              <DeltaButton
                data-testid="leverage-modal"
                onClick={handleLeverageClick}
                className={styles.leverageBtn}
                wrapperClassName={`${styles['selected-leverage']}`}
                text={`${getFloor(selectedLeverage)}x`}
              />
            </Render>
          </div>
          {/* </MediaQuery> */}
          {/* <MediaQuery query={device.down.md}>
            <div
              tabIndex={0}
              role="button"
              onKeyDown={() => {}}
              data-testid="leverage-modal-mobile"
              onClick={handleMarginModeOpen}
              className={styles.mobileLeverageBtn}
            >
              <span className="me-1">
                {marginType === MARGIN_TYPES.ISOLATED
                  ? t(`trading:openPositionCols.isolated`)
                  : t(`trading:openPositionCols.cross`)}
              </span>
              {!isOptionsBuyAction && (
                <span className={`ms-1 ${styles['selected-leverage']}`}>
                  {getFloor(selectedLeverage)}x
                </span>
              )}
            </div>
          </MediaQuery> */}
        </>
      </Render>

      <Render when={marginType === MARGIN_TYPES.ISOLATED && !IS_INDIAN_EXCHANGE}>
        <div className={styles.autoMarginContainer}>
          <PopoverWithCustomIcon
            iconComponent={
              <div className={styles.autoMarginText} data-palette="LeverageSection">{t('trading:autoMargin')}</div>
            }
            popoverComponent={
              <OrderSectionPopoverContent
                text={
                  <>
                    <div data-palette="LeverageSection">{t('trading:autoMarginTooltip')}</div>
                    <div
                      tabIndex={0}
                      role="button"
                      onKeyDown={() => {}}
                      data-testid="auto-topup"
                      onClick={() => {
                        window.open(PORTFOLIO_MARGIN_GUIDE_LINK, '_blank').focus();
                      }}
                      className={styles.learnMoreTitle}
                      data-palette="LeverageSection">
                      <span className="me-1 mt-1">{t('common:learnMore')}</span>
                      <LearnMoreIcon height={12} width={12} />
                    </div>
                  </>
                }
              />
            }
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          />
          <div className={styles.switchContainer}>
            <MuiSwitch
              checked={allPreferences.default_auto_topup}
              onChange={handleAutoMarginChange}
              data-testid="auto-topup-switch"
            />
          </div>
        </div>
      </Render>
      <Render when={marginType === MARGIN_TYPES.PORTFOLIO}>
        <RenderForGlobalExchange>
          <PortfolioMargin
            showModal={showModal}
            openPopup={handleMarginModeOpen}
            enabled_portfolios={eligiblePortfolios}
          />
        </RenderForGlobalExchange>
      </Render>
      <Render when={marginType === MARGIN_TYPES.CROSS}>
        <CrossPlaceOrderSection openPopup={handleMarginModeOpen} />
      </Render>
    </div>
    {/* {(!isOptionsBuyAction || marginType === 'portfolio') && (
    )} */}
    {/* We have removed the above condition and got the modal out bcz user should be able to updated to portfolio margin if required */}
    {showModal && (
      <ChangeMarginModePopUp
        handleMarginModeClose={handleMarginModeClose}
        marginType={marginType}
        isolatedError={isolatedError}
        updateMarginModeFun={updateMarginModeFun}
        resetIsolatedError={resetIsolatedError}
        showModal={showModal}
      />
    )}
    <Render when={showLeverageModal}>
      <MaterialDialog
        showCancel={false}
        marginTopInherit={!isMobileOrTablet()}
        transition={isMobileOrTablet()}
        fullWidth={isMobile}
        title=""
        width={400}
        open={showLeverageModal}
        contentClassName={styles.leverageModal}
        onClose={handleMarginModeClose}
      >
        {/* eslint-disable-next-line test-selectors/onSubmit */}
        <Leverage side={side} onCancel={handleLeverageModalClose} />
      </MaterialDialog>
    </Render>
  </>);
};

export { ChangeMarginModePopUp, LeverageDropdown };

export default memo(LeverageSection);
